'use client'
import { faUser, faSignOutAlt, faUsers } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useAtom } from 'jotai'
import Link from 'next/link'
import { useState, useRef, useEffect } from 'react'

import { UserRole } from '@/graphql/generated-types'
import { sessionAtom } from '@/shared-state/session'
import { exitToLogin } from '@/utils/misc'

const THEME_STORAGE_KEY = 'spoton-theme'
const THEMES = [
  { value: 'grayish', label: 'Grayish' },
  { value: 'greenstadium', label: 'Green Stadium' },
  { value: 'dim', label: 'Dim' },
  { value: 'magicalsky', label: 'Magical Sky' }
] as const

export default function UserSetting() {
  const [session, setSession] = useAtom(sessionAtom)
  const [isOpen, setIsOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const [theme, setTheme] =
    useState<(typeof THEMES)[number]['value']>('greenstadium')

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  useEffect(() => {
    if (typeof window === 'undefined') return
    const saved = window.localStorage.getItem(THEME_STORAGE_KEY)
    const fromDom = document.documentElement.getAttribute('data-theme')
    const candidate = saved || fromDom || 'greenstadium'
    if (THEMES.some(t => t.value === candidate)) {
      setTheme(candidate as (typeof THEMES)[number]['value'])
    }
  }, [])

  const applyTheme = (nextTheme: (typeof THEMES)[number]['value']) => {
    setTheme(nextTheme)
    if (typeof window === 'undefined') return
    window.localStorage.setItem(THEME_STORAGE_KEY, nextTheme)
    document.documentElement.setAttribute('data-theme', nextTheme)
  }

  const handleLogout = () => {
    // Clear session
    setSession(undefined)
    // Clear localStorage token
    if (typeof window !== 'undefined') {
      localStorage.removeItem('token')
      localStorage.removeItem('spoton-user')
      localStorage.removeItem('location')
      localStorage.removeItem('customer')
    }
    // Redirect to login
    exitToLogin()
  }

  const userName = session?.user?.userName || 'User'
  /** Persisted sessions sometimes omit `user`; `role` is set at login on the session root. */
  const loginRole = session?.user?.role ?? session?.role

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        className="btn btn-ghost btn-circle"
        onClick={() => setIsOpen(!isOpen)}
        aria-label="User menu"
      >
        <FontAwesomeIcon icon={faUser} className="text-xl" />
      </button>

      {isOpen && (
        <div className="absolute right-0 top-full mt-2 w-48 bg-base-100 rounded-lg shadow-lg border border-base-300 z-50">
          <div className="py-2">
            <div className="px-4 py-2 text-sm text-base-content/70 border-b border-base-300">
              {userName}
            </div>
            <div className="px-4 py-2 text-sm border-b border-base-300">
              <div className="text-xs font-semibold text-base-content/70">
                Theme
              </div>
              <select
                className="select select-bordered select-sm w-full mt-2"
                value={theme}
                onChange={event =>
                  applyTheme(
                    event.target.value as (typeof THEMES)[number]['value']
                  )
                }
                aria-label="Theme"
              >
                {THEMES.map(t => (
                  <option key={t.value} value={t.value}>
                    {t.label}
                  </option>
                ))}
              </select>
            </div>
            {loginRole === UserRole.Admin && (
              <Link
                href="/users"
                className="w-full text-left px-4 py-2 text-sm hover:bg-base-200 flex items-center gap-2"
                onClick={() => setIsOpen(false)}
              >
                <FontAwesomeIcon icon={faUsers} className="text-sm" />
                Manage users
              </Link>
            )}
            {loginRole === UserRole.LocationAdmin && (
              <Link
                href="/users"
                className="w-full text-left px-4 py-2 text-sm hover:bg-base-200 flex items-center gap-2"
                onClick={() => setIsOpen(false)}
              >
                <FontAwesomeIcon icon={faUsers} className="text-sm" />
                Add location users
              </Link>
            )}
            <button
              className="w-full text-left px-4 py-2 text-sm hover:bg-base-200 flex items-center gap-2"
              onClick={handleLogout}
            >
              <FontAwesomeIcon icon={faSignOutAlt} className="text-sm" />
              Logout
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
